import React, { useState, useEffect } from 'react';
import { Card } from "@shared/ui/card";
import { ValueLabel } from "@shared/components/ValueLabel";
import { Droplet, CornerDownRight, Signal, Activity, Info, AlertTriangle, Trash2, Zap, LayoutList } from "lucide-react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@shared/ui/accordion";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { getLoginDataFromStorage } from "@shared/utils/loginUtils";
import { useConsumerDetail, useMeterList } from "../../../hooks";
import { useTranslation } from "react-i18next";

export const ServicesMetersLanding = () => {
  const { t } = useTranslation();
  const { remoteUtilityId, remoteConsumerNumber } = getLoginDataFromStorage();

  const historyColumns: TableColumn<any>[] = React.useMemo(() => [
    { key: "readDate", header: t("Read Date") },
    { key: "registerReading", header: t("Register Reading") },

    {
      key: "readType", header: t("Read Type"), render: (val: any) => (
        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${val === 'Manual' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
          {val}
        </span>
      )
    },
  ], [t]);

  const { data: response, isLoading } = useConsumerDetail({
    remote_utility_id: Number(remoteUtilityId),
    consumer_no: remoteConsumerNumber,
  });

  const consumerData = response?.result;
  const meters = consumerData?.consumerMappingData || [];

  const [activeUtilityReadings, setActiveUtilityReadings] = useState<{ [key: string]: any[] }>({});
  const [currentFetchingUtility, setCurrentFetchingUtility] = useState<string | null>(null);

  const currentMapping = meters.find((mapping: any) => mapping.remoteMeterId === currentFetchingUtility);

  const meterListQuery = useMeterList({
    remote_utility_id: currentMapping ? consumerData?.remoteUtilityId : null,
    consumer_id: currentMapping ? consumerData?.id : null,
    remote_meter_id: currentMapping ? currentMapping.remoteMeterId : null,
    is_status: false,
    page: 1,
    limit: 10,
  });

  const transformApiDataToReadings = (apiResults: any[], utilityService: string): any[] => {
    const unit = utilityService === 'Electricity' ? 'kWh' :
      (utilityService === 'Water' || utilityService === 'Hot Water') ? 'L' : 'm³';

    return apiResults.map(result => ({
      id: result.id || Math.random().toString(),
      readDate: result.meter?.currentReadingDate || result.createdDate || result.updatedOn || 'NA',
      registerReading: (result.meter_reading ?? result.meterReading) != null ? `${result.meter_reading ?? result.meterReading} ${unit}` : 'NA',
      readType: result.meter?.status || 'NA',
    }));
  };
  const fetchUtilityReadings = (utilityId: string) => {
    if (activeUtilityReadings[utilityId]) return;
    setCurrentFetchingUtility(utilityId);
  };

  useEffect(() => {
    if (currentFetchingUtility && meterListQuery.data) {
      const mapping = meters.find((m: any) => m.remoteMeterId === currentFetchingUtility);

      if (mapping && meterListQuery.data.results) {
        const transformedReadings = transformApiDataToReadings(
          meterListQuery.data.results,
          mapping.utilityService
        );

        setActiveUtilityReadings(prev => ({
          ...prev,
          [currentFetchingUtility]: transformedReadings
        }));
      }
      setCurrentFetchingUtility(null);
    }
  }, [meterListQuery.data, currentFetchingUtility, meters]);

  const getServiceStyles = (service: string) => {
    const s = service?.toLowerCase() || '';
    const config = [
      { key: 'hot water', icon: Droplet, colorClass: 'red', textClass: 'text-red-600', bgClass: 'bg-red-50', borderClass: 'border-red-100' },
      { key: 'water', icon: Droplet, colorClass: 'blue', textClass: 'text-blue-600', bgClass: 'bg-blue-50', borderClass: 'border-blue-100' },
      { key: 'sewer', icon: Activity, colorClass: 'teal', textClass: 'text-teal-600', bgClass: 'bg-teal-50', borderClass: 'border-teal-100' },
      { key: 'waste', icon: Activity, colorClass: 'teal', textClass: 'text-teal-600', bgClass: 'bg-teal-50', borderClass: 'border-teal-100' },
      { key: 'trash', icon: Trash2, colorClass: 'emerald', textClass: 'text-emerald-600', bgClass: 'bg-emerald-50', borderClass: 'border-emerald-100' },
      { key: 'solid', icon: Trash2, colorClass: 'emerald', textClass: 'text-emerald-600', bgClass: 'bg-emerald-50', borderClass: 'border-emerald-100' },
      { key: 'electric', icon: Zap, colorClass: 'amber', textClass: 'text-amber-600', bgClass: 'bg-amber-50', borderClass: 'border-amber-100' },
    ].find(c => s.includes(c.key));

    return config || { icon: Activity, colorClass: 'slate', textClass: 'text-slate-600', bgClass: 'bg-slate-50', borderClass: 'border-slate-100' };
  };


  if (meters.length === 0) {
    return <div className="p-8 text-center text-slate-500">{t("No meters found for this account.")}</div>;
  }

  return (
    <div className="flex flex-col gap-8 w-full pb-10">
      {meters.map((meterData: any, index: number) => {
        const { utilityService, statusDisplay, meterDetails, isActive } = meterData;
        const styles = getServiceStyles(utilityService);
        const Icon = styles.icon;

        return (
          <section key={meterDetails?.id || index}>
            <div className={`flex items-center gap-2 mb-4 ml-1 ${styles.textClass}`}>
              <Icon className="w-4 h-4" />
              <h3 className="text-sm font-bold tracking-wide">{utilityService} {t("Meter")}</h3>
            </div>

            <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
              {/* Header */}
              <div className="p-6 flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 border ${styles.bgClass} ${styles.textClass} ${styles.borderClass}`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <h4 className="text-[16px] font-bold text-slate-900">{meterDetails?.meterNumber || 'NA'}</h4>
                      <span className={`px-2.5 py-0.5 rounded-md text-[10px] font-bold tracking-wide ${isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'}`}>
                        {statusDisplay || 'NA'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                      <Signal className="w-3.5 h-3.5" />
                      {meterDetails?.meterTypeDisplay || 'NA'}
                    </div>
                  </div>

                  <div className="mt-1.5 flex flex-col gap-0.5">
                    <div className="text-[13px] font-semibold text-slate-700">{meterDetails?.productCode || utilityService || 'NA'}</div>
                    <div className="text-xs text-slate-400 flex items-center gap-1">
                      <CornerDownRight className="w-3 h-3 text-slate-300" />
                      {meterDetails?.premise?.split('_')?.pop() || 'NA'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Properties Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-6 px-6 py-5 border-y border-slate-100 bg-slate-50/50">
                {[
                  { label: t("Meter Number"), value: meterDetails?.meterNumber },
                  { label: t("Device No"), value: meterDetails?.deviceNo },
                  { label: t("Meter Type"), value: meterDetails?.meterTypeDisplay },
                  { label: t("Location"), value: meterDetails?.premise?.split('_')?.pop() },
                  { label: t("Physical Status"), value: meterDetails?.physicalStatusDisplay },
                  { label: t("Last Read Date"), value: meterDetails?.lastReadingDate },
                  { label: t("Last Read Value"), value: meterDetails?.lastReading },
                  { label: t("Reading Status"), value: meterDetails?.readingStatusDisplay },
                ].map((prop, idx) => (
                  <ValueLabel
                    key={idx}
                    label={prop.label}
                    value={prop.value ?? 'NA'}
                  />
                ))}
              </div>

              {/* Reading History Accordion */}
              <div className="px-6 pb-6 mt-4">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="history" className="border-2 border-slate-900 rounded-xl bg-white shadow-sm overflow-hidden">
                    <AccordionTrigger
                      className="hover:no-underline py-3 px-4 bg-slate-50/50"
                      onClick={() => fetchUtilityReadings(meterData.remoteMeterId)}
                    >
                      <div className="flex items-center gap-2 text-[13px] font-bold text-blue-700">
                        <LayoutList className="w-4 h-4" /> {t("View Reading History")}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-0 pb-0 border-t-2 border-slate-900">
                      <div className="overflow-x-auto">
                        {activeUtilityReadings[meterData.remoteMeterId] ? (
                          activeUtilityReadings[meterData.remoteMeterId].length > 0 ? (
                            <GenericTable
                              data={activeUtilityReadings[meterData.remoteMeterId]}
                              columns={historyColumns}
                              rowKey="id"
                            />
                          ) : (
                            <div className="p-6 text-center text-slate-500">{t("No reading history found.")}</div>
                          )
                        ) : (
                          <div className="p-6 text-center text-slate-500">
                            {meterListQuery.isFetching && currentFetchingUtility === meterData.remoteMeterId
                              ? t("Loading reading history...")
                              : t("Click to load reading history.")}
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </Card>
          </section>
        );
      })}
    </div>
  );
};
